<template>
    <div
        class="video-preview"
        role="button"
        tabindex="0"
        :aria-label="translations.watch"
        @click="openModal"
        @keydown.enter="openModal"
        @keydown.space.prevent="openModal"
    >
        <img :src="thumbnailSrc" :alt="thumbnailAlt" class="video-preview__thumbnail" />
        <div class="video-preview__overlay" />
        <div class="video-preview__play-button">
            <div class="video-preview__play-icon">
                <svg
                    class="video-preview__play-svg"
                    viewBox="0 0 21 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M20 11L0.5 21.5V0.5L20 11Z" fill="currentColor" />
                </svg>
            </div>
            <span class="video-preview__play-label typographies-paragraph-caps-small-black">
                {{ translations.watch }}
            </span>
        </div>
    </div>
    <Teleport to="body">
        <Transition name="video-preview-fade">
            <div
                v-if="isModalOpen"
                ref="modalRef"
                class="video-preview-modal"
                tabindex="-1"
                @click.self="closeModal"
                @keydown.esc="closeModal"
            >
                <div class="video-preview-modal__content">
                    <iframe
                        :src="embedUrl"
                        :title="translations.watch"
                        class="video-preview-modal__iframe"
                        allow="autoplay; fullscreen"
                        allowfullscreen
                    />
                </div>

                <CompassTooltip
                    :content="translations.close"
                    :placement="CompassEnums.TooltipPosition.LEFT"
                    class="video-preview-modal__tooltip"
                    content-class="video-preview-modal__tooltip-item"
                >
                    <CompassIconButton
                        icon="close"
                        :type="CompassEnums.IconButtonType.SECONDARY"
                        :variant="CompassEnums.IconButtonVariant.CONTRAST"
                        @click="closeModal"
                    />
                </CompassTooltip>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, inject } from 'vue';
import { Translator } from 'types/injects/translator.type';

interface Props {
    thumbnailSrc: string;
    videoUrl: string;
    thumbnailAlt?: string;
}

const props = withDefaults(defineProps<Props>(), {
    thumbnailAlt: '',
});

const $t = inject('$t') as Translator;

const translations: Record<string, string> = {
    watch: $t.__('common.labels.watch'),
    close: $t.__('common.actions.close'),
};

const isModalOpen = ref(false);
const modalRef = ref<HTMLElement | null>(null);

const getEmbedUrl = (url: string): string => {
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
    if (vimeoMatch) {
        const videoId = vimeoMatch[1];
        const hash = vimeoMatch[2];
        const hashParam = hash ? `?h=${hash}&autoplay=1` : '?autoplay=1';
        return `https://player.vimeo.com/video/${videoId}${hashParam}`;
    }
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
        return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`;
    }
    if (url.startsWith('https://')) {
        return url;
    }
    return '';
};

const embedUrl = computed((): string => {
    return getEmbedUrl(props.videoUrl);
});

const openModal = async (): Promise<void> => {
    isModalOpen.value = true;
    document.body.style.overflow = 'hidden';
    await nextTick();
    modalRef.value?.focus();
};

const closeModal = (): void => {
    isModalOpen.value = false;
    document.body.style.overflow = '';
};
</script>

<style lang="scss" scoped>
.video-preview {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: var(--spacing-1);
    cursor: pointer;
    &__thumbnail {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.5s ease-in-out;
        .video-preview:hover & {
            transform: scale(1.05);
        }
    }
    &__overlay {
        position: absolute;
        inset: 0;
        background: var(--colors-common-background-overlay-light);
    }
    &__play-button {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 68px;
        max-width: 68px;
        height: 68px;
        border-radius: 50%;
        background: var(--colors-common-background-darkest-default);
        overflow: hidden;
        transition: all 0.5s ease 0.15s;
        .video-preview:hover & {
            max-width: 200px;
            height: 40px;
            border-radius: 30px;
            background: var(--colors-common-background-default);
            padding-right: var(--spacing-2);
            justify-content: flex-start;
            gap: var(--spacing-1);
            transition: all 0.5s ease;
        }
    }
    &__play-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        padding-left: 3px;
        color: var(--colors-common-background-default);
        transition:
            background 0.5s ease 0.15s,
            margin 0.5s ease 0.15s;
        .video-preview:hover & {
            background: var(--colors-common-background-darkest-default);
            margin-left: -1px;
            transition:
                background 0.5s ease,
                margin 0.5s ease;
        }
    }
    &__play-svg {
        width: 20px;
        height: 20px;
        transition:
            width 0.5s ease 0.15s,
            height 0.5s ease 0.15s;

        .video-preview:hover & {
            width: 12px;
            height: 12px;
            transition:
                width 0.5s ease,
                height 0.5s ease;
        }
    }
    &__play-label {
        max-width: 0;
        opacity: 0;
        overflow: hidden;
        white-space: nowrap;
        color: var(--colors-common-background-darkest-default);
        transition:
            max-width 0.15s ease,
            opacity 0.1s ease;
        .video-preview:hover & {
            max-width: 100px;
            opacity: 1;
            transition:
                max-width 0.5s ease,
                opacity 0.2s ease 0.1s;
        }
    }
}
.video-preview-modal {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--colors-common-background-overlay-dark);

    &__content {
        width: 952px;
        max-width: 90vw;
        aspect-ratio: 16 / 9;
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-0);
        box-shadow: var(--elevation-1);
        overflow: hidden;
    }

    &__iframe {
        width: 100%;
        height: 100%;
        border: none;
    }

    &__tooltip {
        position: absolute;
        top: var(--spacing-6);
        right: var(--spacing-6);
    }
}

.video-preview-fade-enter-active,
.video-preview-fade-leave-active {
    transition: opacity 0.2s ease;
}

.video-preview-fade-enter-from,
.video-preview-fade-leave-to {
    opacity: 0;
}
</style>

<style lang="scss">
.video-preview-modal__tooltip-item {
    // !important is used as a workaround for stacking layer issues caused by
    // <Teleport to="body"> in the tooltip
    z-index: 10000 !important;
}
</style>
